import { useEffect, useMemo, useRef, useState } from 'react';
import { kindLabel } from '../lib/driverKind';
import { useLocation } from 'react-router-dom';
import { api, type BillGroupSum, type FinGrpOption, type ListResult, type SalaryRemain } from '../lib/api';
import { DataTable, ErrorBox, Pager, loadAllPages, usePageSize, dash, money, useList, type Column, type Row, type SubtotalRow } from '../components/DataTable';
import CustBillDetail from './CustBillDetail';
import SettleClose from './SettleClose';
import SalaryGenPanel from './SalaryGenPanel';
import SettleStatus from './SettleStatus';
import SettleSummary from './SettleSummary';
import SettleMisu from './SettleMisu';
import SettleCommute, { CommuteMonthBar } from './SettleCommute';
import DriveMonPicker from '../components/DriveMonPicker';
import { pickDriveMon, useStickyDriveMon } from '../lib/driveMon';
import { useAutoSearch } from '../lib/search';
import { useSort } from '../lib/sort';
import MonthSearchHint from '../components/MonthSearchHint';
import Modal from '../components/Modal';
import MisuEditForm, { misuAccess } from '../components/MisuEditForm';
import BillTimeline from '../components/BillTimeline';
import { canScreen, useMe } from '../lib/me';
import SalaryPublishPanel from './SalaryPublishPanel';
import { InvoiceRequestForm } from './InvoiceRequest';
import { urlParam } from '../lib/deepLink';
// 🔴 기사앱 명세서 **본체 그대로**. 이름이 이 파일의 담당자용 `SalarySlip` 과 겹쳐 별칭을 쓴다.
import DriverSlip, { type SlipGroup } from '../driver/SalarySlip';
import { AllocChangeBanner } from '../components/AllocChangeAlerts';
import { useWide } from '../lib/phone';
import { useSticky } from '../lib/sticky';

const asRows = <T,>(p: Promise<ListResult<T>>) => p as unknown as Promise<ListResult<Row>>;
/**
 * 통근 운행일수 탭은 **아직 줄이 없는 달**(이번 달·다음 달)도 골라야 한다 — 그 달 줄을 만드는 화면이라서.
 * 정산 월 목록은 데이터가 있는 달만 주므로 여기서 얹는다(KST 기준).
 */
function withCommuteMonths(months: readonly string[]): string[] {
  const kst = new Date(Date.now() + 9 * 3600_000);
  const ym = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  const next = new Date(Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth() + 1, 1));
  return [...new Set([ym(next), ym(kst), ...months])].sort().reverse();
}

/** 거래처별 정산은 통근 줄이 **그 달을 열 때** 생기므로 이번 달(KST)을 목록에 얹는다(데이터가 아직 없어도 고를 수 있게). */
function withThisMonth(months: readonly string[]): string[] {
  const kst = new Date(Date.now() + 9 * 3600_000);
  const cur = `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, '0')}`;
  return [...new Set([cur, ...months])].sort().reverse();
}

/**
 * 거래처별 정산 거르기 — 접수구분·청구법인(게시판 #117 배상준 「현 버스인처럼 접수구분·청구법인 필터」).
 * 브라우저에 기억한다(정산년월처럼 담당자가 늘 같은 축을 본다). 사생활 보호 모드 등에서 저장소가 던지면 「전체」.
 * 거르기는 **서버가** 한다(목록이 쪽으로 나뉘어 화면에서 거르면 「이 쪽만」이 된다 · issues 107) — 총계 줄도 같은 기준.
 */
type CustPick = { recvType: '' | '일반' | '통근'; finGrp: '' | number };
const CUST_PICK_KEY = 'settle.cust.pick';
function readCustPick(): CustPick {
  try {
    const v = JSON.parse(localStorage.getItem(CUST_PICK_KEY) ?? '{}') as Partial<CustPick>;
    return {
      recvType: v.recvType === '일반' || v.recvType === '통근' ? v.recvType : '',
      finGrp: typeof v.finGrp === 'number' && Number.isInteger(v.finGrp) && v.finGrp >= 0 ? v.finGrp : '',
    };
  } catch {
    return { recvType: '', finGrp: '' };
  }
}
/** 실질수익 칸 — 그 달 관리기사A 급여명세가 아직 없어 직영비용을 나눌 수 없을 때. */
function NoSalary() {
  return <span className="text-ink-faint" title="그 달 관리기사A 급여명세 생성 전 — 명세를 만들면 계산됩니다">—</span>;
}

function saveCustPick(p: CustPick) {
  try { localStorage.setItem(CUST_PICK_KEY, JSON.stringify(p)); } catch { /* 기억 못 해도 이번 화면에선 걸린다 */ }
}

/**
 * 정산 조회.
 *
 * ⚠️ **청구액은 저장된 값이 아니다.** 조회할 때마다 계산한다(레거시도 그렇다).
 *    계산식은 `db/schema/_views_settle.sql` 의 뷰 하나에만 있고, 화면은 표시만 한다.
 *    `pnpm db:settle` 이 레거시 MSSQL 과 대조한다 — 2026-07-22 기준 전 지표 일치.
 *
 * ⚠️ 금액은 문자열로 받는다. 화면에서 Number 로 바꾸는 건 **자릿수 표시용**이고
 *    그 값으로 더하거나 비교하지 않는다(합계는 전부 SQL 이 낸다).
 */

/**
 * 거래처별 정산 칼럼. 청구상태 칸 — 청구 전이면 「청구 요청」(발행 요청 창 · 계산서·카드·현금),
 * 그 밖은 「청구 정보」(보기 · 「취소·수정 승인요청」). 10-16 회의 #36: 청구대상 직접 표시는 없앴다.
 */
/** 좌우 2단(넓은 화면 · 상세 열림)일 때 왼쪽 목록에 남길 칸. */
const SPLIT_KEYS = ['recv_id', 'drive_mon', 'cust_name', 'cost_cust_total', 'misu_req_status'];

/** 통합 청구 묶음 합계(#489 · 서버 값 그대로) → 표의 접는 머리 줄. 금액 칸 key 가 목록 칸 key 와 같아 제자리에 선다. */
const toBillGroupRow = (g: BillGroupSum): SubtotalRow => {
  const { key, group_id: _id, name, drive_mon: _mon, count, ...values } = g;
  return { key, count: Number(count ?? 0), label: String(name ?? ''), values };
};

const custColumns = (onMisu: (r: Row) => void, canMark: boolean, onInvReq: (r: Row) => void, onTimeline: (r: Row) => void): Column[] => [
  // 접수 NO = 레거시 RecvId 와 같은 번호(배차 화면의 접수 NO 와 같은 값). 한 줄 = 접수 × 정산월이다.
  // phone: 폰에선 거래처를 왼쪽에 고정하고 번호·보조 숫자는 뺀다(금액이 한 화면에 들어오게 · DataTable `phone`).
  { key: 'recv_id', label: '접수 NO', align: 'right', phone: 'hide', render: (r) => <span className="text-xs text-ink-soft">{dash(r.recv_id)}</span> },
  { key: 'drive_mon', label: '정산월' },
  {
    key: 'recv_type',
    label: '구분',
    render: (r) => {
      const v = dash(r.recv_type);
      if (v === '—') return <span className="text-ink-faint">—</span>;
      return (
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
            v === '통근' ? 'bg-violet-50 text-violet-700' : 'bg-canvas text-ink-soft'
          }`}
          title={r.from_snapshot === true ? '정산 스냅샷에서 읽은 확정 금액' : '배차에서 계산한 금액'}
        >
          {v}
          {r.from_snapshot === true && <span className="ml-1 opacity-60">확정</span>}
        </span>
      );
    },
  },
  // 담당자 = 버스인 접수담당자(#497 · 조회 범위·머리줄 담당자 고르기와 같은 값). 「거래처 정산 담당자」(fin_manager)는 거래처 쪽 사람이다.
  // key 는 계정에 저장된 칸 순서·폭 때문에 옛 이름 그대로.
  { key: 'fin_manager', label: '버스인접수담당자', phone: 'hide', render: (r) => <span className="text-xs">{dash(r.owner_name)}</span> },
  // 거래처명은 한 줄(긴 이름이 줄바꿈되면 줄 높이가 들쭉날쭉해 「어느 줄」인지 놓친다 — 10-29 way). 전체 이름은 마우스를 올리면.
  {
    key: 'cust_name',
    label: '거래처',
    phone: 'pin',
    render: (r) => <span className="block max-w-[18rem] truncate" title={String(r.cust_name ?? '')}>{dash(r.cust_name)}</span>,
  },
  { key: 'dispt_cnt', label: '배차', align: 'right', phone: 'hide' },
  { key: 'cost_cust', label: '청구액', align: 'right', render: (r) => money(r.cost_cust) },
  { key: 'cust_plus', label: '추가', align: 'right', phone: 'hide', render: (r) => money(r.cust_plus) },
  { key: 'cust_minus', label: '차감', align: 'right', phone: 'hide', render: (r) => money(r.cust_minus) },
  {
    key: 'cost_cust_total',
    label: '수입계',
    align: 'right',
    render: (r) => <b>{money(r.cost_cust_total)}</b>,
  },
  { key: 'cost_driver_total', label: '지출계', align: 'right', render: (r) => money(r.cost_driver_total) },
  {
    key: 'profit',
    label: '수익',
    align: 'right',
    render: (r) => {
      const v = Number(r.profit ?? 0);
      return <span className={v < 0 ? 'text-red-600' : ''}>{money(r.profit)}</span>;
    },
  },
  /**
   * 실질수익(10-21 way) = 수익 − 관리기사A 차량비용 배분. 「수익」은 레거시대로 관리기사A 운행료를 빼기만 해서
   * 그 차량의 급여·유류·수리·할부가 어디에도 안 붙는다 — 직영 수익 시스템과 같은 식으로 나눠 붙인다
   * (서버 `functions/src/directCost.ts`). 그 달 관리기사A 급여명세가 아직 없으면 「—」(0 이면 부풀려진 채 맞아 보인다).
   */
  {
    key: 'direct_cost',
    label: '직영비용',
    align: 'right',
    phone: 'hide',
    render: (r) => (r.direct_cost == null ? <NoSalary /> : money(r.direct_cost)),
    totalRender: (t) => (t.direct_cost == null ? <NoSalary /> : money(t.direct_cost as string)),
  },
  {
    key: 'real_profit',
    label: '실질수익',
    align: 'right',
    render: (r) => {
      if (r.real_profit == null) return <NoSalary />;
      const v = Number(r.real_profit);
      return <b className={v < 0 ? 'text-red-600' : ''}>{money(r.real_profit)}</b>;
    },
    totalRender: (t) => {
      if (t.real_profit == null) return <NoSalary />;
      const v = Number(t.real_profit);
      return <b className={v < 0 ? 'text-red-600' : ''}>{money(t.real_profit as string)}</b>;
    },
  },
  { key: 'fin_req_method', label: '청구방법' },
  {
    key: 'misu_req_status',
    label: '청구상태',
    /**
     * ⚠️ 예전엔 `has_misu`(= `result_misu` **행 존재 여부**)로 빨간 '미수' 배지를 찍었다.
     *    그 표는 미수 원장이 아니라 **청구·수금 상태 원장**이라 입금완료된 건도 참이었다 —
     *    실측 2026-08-01: 배지 27,096건 중 **13,922건(51.4%)이 이미 입금된 건**이었다.
     *
     * 이제 **상태값을 그대로** 보여준다. "미수/아님" 이분법보다 '청구완료'·'입금예정' 이
     * 바로 읽히고, 정보를 버리지 않으면서 오탐이 사라진다.
     *
     * ⚠️ **색은 상태 글자가 아니라 `is_misu` 가 정한다.** 2026-08-01 사용자 확정으로 미수 판정이
     *    **입금일 미입력**으로 바뀌었다 — `req_status` 는 청구 진행 상태라 입금 후에도 '청구완료'로
     *    남는다(실측: 그런 행이 11,653건). 그래서 **'청구완료'가 초록으로 뜰 수 있고 그게 맞다.**
     *
     * 2026-10-02 way 「예전에는 거래처별 정산에서 청구상태, 입금상태를 볼 수 있었거든」 —
     * 한 칸(청구·수금)에 묶고 입금상태를 툴팁에 숨겼던 것을 레거시처럼 **두 칸으로** 폈다(입금상태는 옆 칸).
     * 그리고 이 칸의 「변경」으로 **여기서 바로** 청구를 표시한다 — 미수금 탭은 원장 줄이 있는 건만
     * 보여서, 아직 줄이 없는 접수·달(예: 6440/2026-10)은 웹 어디서도 청구를 걸 수 없었다.
     * 선지급은 그 청구(청구대상·청구완료 또는 입금)가 있어야 열린다.
     * 🔴 같은 날 오후 way 「거래처별 정산은 영업부 전담화면이라 청구대상만 · 입금체크도 회계팀이라 조회만 ·
     *    미수금에서 다 관리」 → 여기서는 **청구대상 표시만**(서버 saveResultMisu scope 'sales' 가 강제).
     *    🔴 10-16 회의 #36 — 그 표시마저 없애고 「청구 요청」(발행 요청 창)으로만 건다. 회계는 넘어온 요청만 처리한다.
     */
    render: (r) => {
      const st = String(r.misu_req_status ?? '').trim();
      const misu = r.is_misu === true;
      const paid = String(r.misu_deposit_date ?? '').trim();
      // 10-19 way — 상태 글자를 누르면 그 접수·월의 청구 진행(요청 → 발행 → 취소·수정 → 입금)을 한 창에(`BillTimeline`).
      const badge =
        st === '' ? (
          <button type="button" className="text-ink-faint hover:underline" title="아직 청구 기록이 없습니다 — 눌러서 청구 진행 보기"
            onClick={(e) => { e.stopPropagation(); onTimeline(r); }}>—</button>
        ) : (
          <button
            type="button"
            className={`rounded px-1.5 py-0.5 text-xs font-medium hover:ring-1 hover:ring-brand-400 ${
              misu ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
            }`}
            title={`${paid ? `입금일: ${paid}` : '입금일 미입력 → 미수'} — 눌러서 청구 진행 보기`}
            onClick={(e) => { e.stopPropagation(); onTimeline(r); }}
          >
            {st}
            {misu && <span className="ml-1">미수</span>}
          </button>
        );
      // 「청구 요청」 = 세금계산서 발행 요청 창(발행방식에 카드·현금도) — 10-16 회의 #36: 회계는 넘어온 청구 요청만 처리하고
      //   청구대상 직접 표시는 없앴다. 올리면 서버가 이 줄을 청구대상 + 청구요청일(오늘)로 건다(invoiceRequest.saveInvoiceRequest).
      //   청구대상인데 요청이 없던 옛 줄(맨 표시만 걸린 것)도 「발행 요청」으로 올릴 수 있게 둔다(같은 접수·달 요청은 서버가 한 번 더 묻는다).
      const invBtn = (st === '' || st === '청구대상') && (
        <button
          type="button"
          className="rounded border border-brand-600 bg-brand-50 px-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100"
          title="이 접수·정산월 청구를 회계팀에 요청합니다(세금계산서·카드·현금) — 올리면 청구 금액이 잠기고 선지급·명세 공제가 열립니다"
          onClick={(e) => { e.stopPropagation(); onInvReq(r); }}
        >
          {st === '' ? '청구 요청' : '발행 요청'}
        </button>
      );
      if (st === '') return <span className="inline-flex items-center gap-1 whitespace-nowrap">{badge}{invBtn}</span>;
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          {badge}
          {invBtn}
          <button
            type="button"
            className="rounded border border-line px-1.5 text-xs text-brand-700 hover:bg-brand-50"
            title={canMark ? '청구상태·청구일·입금상태·입금일·메모·이력 보기 — 청구 금액 수정·청구 취소는 「취소·수정 승인요청」'
              : '청구상태·청구일·입금상태·입금일·메모·이력 보기'}
            // 줄 클릭(상세 펼치기)과 겹치지 않게 막는다.
            onClick={(e) => { e.stopPropagation(); onMisu(r); }}
          >
            청구 정보
          </button>
        </span>
      );
    },
  },
  {
    key: 'misu_bank_status',
    label: '입금상태',
    // 폰에선 뺀다 — 청구상태 배지 색(입금일 기준 미수)이 같은 질문에 답하고, 금액 칸 자리가 먼저다.
    phone: 'hide',
    render: (r) => {
      const bank = String(r.misu_bank_status ?? '').trim();
      const paid = String(r.misu_deposit_date ?? '').trim();
      if (bank === '' && paid === '') return <span className="text-ink-faint">—</span>;
      return (
        <span className="whitespace-nowrap text-xs" title={paid ? `입금일: ${paid}` : '입금일 미입력'}>
          {bank || '—'}
          {paid && <span className="ml-1 text-ink-faint">{paid}</span>}
        </span>
      );
    },
  },
];

const SALARY_COLUMNS: Column[] = [
  { key: 'drive_mon', label: '정산월' },
  { key: 'kind', label: '구분' },
  { key: 'driver_name', label: '기사', phone: 'pin' },
  { key: 'driver_kind', label: '기사구분', render: (r) => kindLabel(r.driver_kind) },
  { key: 'owner_name', label: '차주' },
  { key: 'grp_name', label: '소속' },
  { key: 'line_cnt', label: '항목', align: 'right', phone: 'hide' },
  { key: 'plus_amt', label: '지급', align: 'right', render: (r) => money(r.plus_amt) },
  { key: 'minus_amt', label: '공제', align: 'right', render: (r) => money(r.minus_amt) },
  {
    key: 'result_amt',
    label: '차인지급액',
    align: 'right',
    render: (r) => <b>{money(r.result_amt)}</b>,
  },
];

/**
 * 명세 줄의 원천 → **그 줄을 등록한 화면**. 실측 분포(전 기간, `salary_month.kind`):
 * Monthly 293,095 · Daily 179,934 · Drive 123,046 · Req 70,072 · FinInst 16,683 · 빈값 3,019.
 * ⚠️ 여기 없는 값이 나오면 '—' 로 둔다 — 모르는 것을 아는 척 이름 붙이지 않는다.
 */
const SALARY_SOURCE: Record<string, string> = {
  Daily: '재무추가',
  Monthly: '월정기',
  FinInst: '할부금',
  Req: '영업정산',
  // 운행료는 사람이 입력하는 화면이 아니라 **배차에서 계산돼 넘어온다** — 그래서 이름을 다르게 적는다.
  Drive: '운행료(배차)',
};

const LINE_COLUMNS: Column[] = [
  // 정산담당자(#285) — 구 버스인 운행료내역 첫 칸 = 그 줄 접수의 정산담당. 노선에 안 매인 줄은 '—'.
  { key: 'fin_manager', label: '거래처정산담당자', phone: 'hide', render: (r) => <span className="text-xs">{dash(r.fin_manager)}</span> },
  // 거래처(#515) — 그 줄 접수의 거래처. 재무추가·월정기·할부금 줄은 접수가 없어 '—'.
  { key: 'cust_name', label: '거래처', phone: 'hide', render: (r) => <span className="text-xs">{dash(r.cust_name)}</span> },
  // 명세 줄의 접수·노선 NO. 운행료·영업정산 줄만 있고 재무추가·월정기·할부금 줄은 노선에 매이지 않아 '—'.
  { key: 'recv_id', label: '접수 NO', align: 'right', render: (r) => <span className="text-xs text-ink-soft">{dash(r.recv_id)}</span> },
  { key: 'dispt_id', label: '노선 NO', align: 'right', render: (r) => <span className="text-xs text-ink-soft">{dash(r.dispt_id)}</span> },
  // 차량·차주(10-02) — 월 중간에 편성이 바뀐 달은 두 편성 줄이 한 장에 섞인다. 어느 쪽 줄인지 보이게.
  {
    key: 'car_no', label: '차량·차주', phone: 'hide',
    render: (r) => <span className="text-xs text-ink-soft">{dash(r.car_no)}{r.owner_name ? ` · ${String(r.owner_name)}` : ''}</span>,
  },
  {
    key: 'io',
    label: '구분',
    // 지급·공제가 한눈에 갈려야 명세서로 읽힌다 — 글자만 다르면 세로로 훑을 때 안 보인다.
    render: (r) => {
      const v = String(r.io ?? '');
      const tone =
        v === '지급' ? 'bg-emerald-50 text-emerald-700'
        : v === '공제' ? 'bg-red-50 text-red-700'
        : 'bg-canvas text-ink-soft';
      return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${tone}`}>{dash(r.io)}</span>;
    },
  },
  {
    key: 'kind',
    label: '입력메뉴',
    /**
     * **이 줄이 어느 화면에서 들어왔나**(2026-08-02 사용자 요청). 고치러 갈 곳이 곧 이 값이다.
     *
     * 예전에는 `category` 를 '분류'로 보여줬는데 그 값은 `Plus`/`Minus` 뿐이라
     * 왼쪽 '구분'(지급/공제)과 **같은 말을 두 번** 하고 있었다(실측 68만 행 중 Plus 24.5만·Minus 44만,
     * 나머지 `Emp` 909행은 2018-01 이전 운행료 잔재다). 칸 하나를 그렇게 쓸 이유가 없다.
     *
     * ⚠️ `kind` 가 빈 줄이 3,019행 있다 — 전부 **2018-01 이전** 이관분이다. '—' 로 둔다.
     */
    render: (r) => {
      const v = String(r.kind ?? '').trim();
      const label = SALARY_SOURCE[v];
      if (!label) return <span className="text-ink-faint" title={v || undefined}>—</span>;
      return (
        <span
          className="rounded bg-canvas px-1.5 py-0.5 text-xs text-ink-soft"
          title={`${label} 화면에서 등록된 줄입니다 (원천 kind=${v})`}
        >
          {label}
        </span>
      );
    },
  },
  { key: 'account_name', label: '계정' },
  {
    key: 'amt',
    label: '금액',
    align: 'right',
    // 공제는 부호를 붙여 보여준다 — 명세서에서 "빼는 돈"이 같은 모양이면 눈이 못 가른다.
    render: (r) => {
      const minus = String(r.io ?? '') === '공제';
      return (
        <span className={minus ? 'text-red-700' : ''}>
          {minus ? '−' : ''}
          {money(r.amt)}
        </span>
      );
    },
  },
  { key: 'comment', label: '비고' },
  {
    key: 'job_user_name',
    label: '입력자',
    /**
     * 그 줄을 만든 사람(2026-08-02 사용자 요청). 원천은 `salary_month.job_user`(로그인 아이디)이고
     * 서버가 `users.login_id` 로 이름을 붙여 준다(실측 매칭률 99.9%).
     * ⚠️ `created_by` 가 아니다 — 전 행이 `busman`(생성기 계정)이라 아무 정보가 없다.
     * ⚠️ 전 기간 채움률 82.5% 라 **빈 줄이 실제로 있다.** '—' 로 두고 없는 이름을 지어내지 않는다.
     */
    render: (r) => (
      <span className="text-xs text-ink-soft" title={r.jobdt ? `입력 ${String(r.jobdt)}` : undefined}>
        {dash(r.job_user_name)}
      </span>
    ),
  },
];

export type SettleTabId = 'cust' | 'salary' | 'status' | 'summary' | 'misu' | 'commute';
/**
 * 세부 탭 — 화면 찾기(#396 · App.tsx)도 이 목록을 쓴다.
 * 2026-09-17 사용자: "거래처별 정산이 더 맞는 말 같아". 탭 id(`cust`)는 그대로 둔다 —
 * 저장된 정산년월·칼럼 설정 키가 그 id 로 붙어 있어 바꾸면 사용자 설정이 초기화된다.
 * ⚠️ 영업정산의 계정 구분 값 `거래처청구`(CustBillDetail·ResultReq)는 **데이터**다. 건드리지 말 것.
 * 「통근 운행일수」 탭(#51·#52)은 09-30 way 재설계로 숨겼다 — 통근 줄은 거래처별 정산에 자동으로 들어오고
 * 편집은 거래처 줄의 「노선·기사 편집」에서 한다(SettleCommute 의 CommuteEditor). 탭 코드는 남겨 둔다.
 */
export const SETTLE_TABS: readonly (readonly [SettleTabId, string])[] = [
  ['cust', '거래처별 정산'],
  ['salary', '기사 급여명세'],
  ['status', '정산현황'],
  // 정산 총괄(10-28 way · 구 버스인 소속별 정산현황·기사정산) — 회계 조회 권한만 탭이 보인다(아래 render).
  ['summary', '정산 총괄'],
  ['misu', '미수금'],
];

export default function Settle() {
  const [tab, setTab] = useState<SettleTabId>('cust');
  const [months, setMonths] = useState<string[]>([]);
  // 고른 정산년월은 화면을 옮겨도 남는다 — 담당자는 한 달을 여러 화면에서 처리한다.
  const [mon, setMon] = useStickyDriveMon();
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('settle', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [detail, setDetail] = useState<Row | null>(null);
  // 명세를 다시 만들면 목록도 다시 읽어야 한다 — 안 그러면 방금 지운 줄이 화면에 남는다.
  const [nonce, setNonce] = useState(0);
  /** 기사 명세서 공개 예약 팝업(마감과 별개 축 — `SalaryPublishPanel` 머리 주석 참고). */
  const [publishOpen, setPublishOpen] = useState(false);
  /** 청구 정보 대상(거래처별 정산 줄). 미수금 탭과 같은 폼 — 여기서는 `scope="sales"`(보기 + 취소·수정 승인요청). */
  const [misuEdit, setMisuEdit] = useState<Row | null>(null);
  const { me } = useMe();
  const canMisu = misuAccess(me, 'sales');
  /** 정산 총괄 = 회계 조회(회계·관리자) — 서버 getSettleSummary 와 같은 게이트. */
  const canSummary = canScreen(me, 'accounting', 'read');
  /**
   * 넓은 화면(2xl+)에서 거래처 줄을 고르면 **좌우 2단** — 왼쪽 좁은 목록(고른 줄 노랗게) · 오른쪽 상세(10-29 way
   * 「접수·노선 클릭할 때 해당건이 그 건이라는 직관성이 떨어진다」). 위아래로 열면 상세를 보려고 스크롤하다 어느 줄인지 놓쳤다.
   * 좁은 화면은 예전대로 표 위에 연다.
   */
  const wide = useWide();
  /** 세금계산서 발행 요청 대상 줄(10-14) — 요청 폼을 그 접수·정산월로 연다. */
  const [invReq, setInvReq] = useState<Row | null>(null);
  /** 청구 진행 창(10-19 — 청구상태 칸을 누르면). */
  const [timeline, setTimeline] = useState<Row | null>(null);
  const custCols = useMemo(() => custColumns(setMisuEdit, canMisu, setInvReq, setTimeline), [canMisu]);

  useEffect(() => {
    api
      .listDriveMonths()
      .then((r) => {
        setMonths(r.rows);
        // 명세서는 원래 "그 달"을 보는 화면이다. 전체 기간을 기본으로 열면
        // 68만 행을 집계해 4초가 걸린다(settle.ts) — 기억된 달, 없으면 최근 월로 시작한다.
        setMon(pickDriveMon(r.rows, mon));
      })
      .catch(() => undefined);
    // 마운트 때 한 번만. `mon` 은 그 시점의 기억된 값을 읽는 용도라 의존성에 넣지 않는다
    // (넣으면 사용자가 달을 고를 때마다 월 목록을 다시 부른다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 소계를 켜면 서버가 **정렬까지 그 키로** 바꾼다 — 안 바꾸면 같은 그룹이 흩어져
  // 소계 줄이 엉뚱한 자리에 선다. 그래서 화면 상태 하나로 둘을 함께 다룬다.
  const [grouped, setGrouped] = useState(false);
  const groupBy = grouped ? (tab === 'cust' ? ('cust' as const) : ('grp' as const)) : undefined;
  /**
   * 통합 청구 묶음으로 보기(#489 이윤규 「코오롱 넷을 한 이름으로 · + 누르면 접수 4개」) — 묶음 멤버 줄을 묶음 한 줄로 접는다.
   * 묶음 합계는 서버가 낸다(내가 보는 멤버만 · 총계는 그대로). 묶음은 거래처를 가로질러 거래처 소계와는 같이 못 켠다.
   */
  const [billGrouped, setBillGrouped] = useState(false);
  const billGroup = tab === 'cust' && billGrouped;
  const [billGroups, setBillGroups] = useState<SubtotalRow[]>([]);
  const [foldOpen, setFoldOpen] = useState<ReadonlySet<string>>(() => new Set());

  // 거래처별 정산 접수구분·청구법인(#117). 고르기 목록은 목록 응답에 같이 온다(정산 화면 권한만으로 — 회계 법인 목록 API 는 회계 권한이라 못 쓴다).
  const [pick, setPickState] = useState<CustPick>(readCustPick);
  const [finGrps, setFinGrps] = useState<FinGrpOption[]>([]);
  function setPick(p: CustPick) {
    setPickState(p);
    saveCustPick(p);
    setPage(0);
    setDetail(null);
  }

  // 거래처별 정산 머리글 정렬(#452 「같은 거래처 모아 보기」)·칸별 검색(#440 「일반/통근 나열」) — 50줄씩 받는 목록이라
  // 둘 다 **서버가** 한다(화면에서 하면 「이 쪽 안에서만」이 된다 · issues 107). 칸별 검색은 치는 대로, 멎으면 적용.
  const [custSort, toggleCustSort] = useSort('settle.cust');
  const [custFilters, setCustFilters] = useState<Record<string, string>>({});
  const [custFilterDraft, setCustFilterDraft] = useState<Record<string, string>>({});
  useAutoSearch(JSON.stringify(custFilterDraft), () => { setCustFilters(custFilterDraft); setPage(0); });

  const args = useMemo(
    () => ({
      q: q || undefined, limit: PAGE, offset: page * PAGE, driveMon: mon || undefined, groupBy,
      ...(tab === 'cust' ? {
        billGroup: billGroup || undefined,
        recvType: pick.recvType || undefined,
        finGrpId: pick.finGrp === '' ? undefined : pick.finGrp,
        sort: custSort?.key,
        dir: custSort?.dir,
        filters: custFilters,
      } : {}),
    }),
    [q, page, PAGE, mon, groupBy, billGroup, tab, pick, custSort, custFilters],
  );

  // 정산현황 탭은 **자기 데이터를 스스로 읽는다**(축·체크필터가 달라서). 여기서 또 부르면
  // 안 쓰는 무거운 집계가 한 번 더 돈다.
  const { rows, loading, error, total, subtotals } = useList(
    (alive) =>
      tab === 'status' || tab === 'summary' || tab === 'misu' || tab === 'commute'
        ? (Promise.resolve({ rows: [], limit: PAGE, offset: 0 }) as unknown as Promise<ListResult<Row>>)
        : tab === 'cust'
          ? asRows(api.listResultCust(args).then((r) => {
              const next = r.finGrps ?? [];
              setFinGrps((cur) => (JSON.stringify(cur) === JSON.stringify(next) ? cur : next));
              if (alive()) setBillGroups((r.billGroups ?? []).map(toBillGroupRow));
              return r;
            }))
          : asRows(api.listSalaryMonths(args)),
    [tab, args, nonce],
  );

  const split = tab === 'cust' && !!detail && wide;
  // #457 — 좌우 2단 왼쪽 폭을 경계선 끌기로(계정에 기억 · 두 번 누르면 기본 32rem).
  const [splitW, setSplitW] = useSticky<number>('settle.cust.split-w', SPLIT_W0, { persist: true, valid: isSplitW });
  const [dragW, setDragW] = useState<number | null>(null);
  const leftW = dragW ?? splitW;
  // 좌우 2단의 왼쪽 목록은 「어느 건인지」 가릴 칸만 — 나머지 금액은 오른쪽 상세 합계에 다 있다.
  const splitCols = useMemo(() => custCols.filter((c) => SPLIT_KEYS.includes(c.key)), [custCols]);
  const detailIdx = detail ? rows.indexOf(detail) : -1;
  const custDetail = tab === 'cust' && detail && (
    <CustBillDetail
      // 접수가 바뀌면 새로 — 앞 접수에서 펴 둔 노선(open)이 남으면 「노선 n/N」 보기가 빈 표가 된다.
      key={`${String(detail.recv_id)}|${String(detail.drive_mon ?? '')}`}
      row={detail}
      onClose={() => setDetail(null)}
      onMisu={setMisuEdit}
      canMarkMisu={canMisu}
      nav={detailIdx < 0 ? undefined : {
        idx: detailIdx,
        count: rows.length,
        go: (d) => { const r = rows[detailIdx + d]; if (r) setDetail(r); },
      }}
    />
  );

  const [topOpen, setTopOpen] = useState(false);

  function switchTab(t: SettleTabId) {
    setTab(t);
    setTerm('');
    setQ('');
    setPage(0);
    setDetail(null);
  }

  /**
   * 미수금 탭 「미청구」 → 거래처별 정산의 그 접수·정산월(2026-10-02). 접수 NO 를 검색어로 넣고
   * 목록이 오면 그 줄의 상세를 연다(`openRecv`). 접수 NO 검색은 거래처 번호에도 걸려 줄이 더 나올 수 있다.
   */
  const [openRecv, setOpenRecv] = useState<{ recvId: number; mon: string } | null>(null);
  function openCust(recvId: number, m: string) {
    switchTab('cust');
    // 건너온 그 줄이 거르기에 걸려 안 보이면 상세가 안 열린다 — 접수구분·청구법인을 「전체」로.
    if (pick.recvType || pick.finGrp !== '') setPick({ recvType: '', finGrp: '' });
    setMon(m);
    setTerm(String(recvId));
    setQ(String(recvId));
    setOpenRecv({ recvId, mon: m });
  }
  // 다른 화면·구글챗 알림에서 건너온 링크(`lib/deepLink.ts` link.misuCancel · link.custBill)면 그 자리부터 —
  // 주소가 바뀔 때 한 번(그 뒤 사람이 고른 탭이 이긴다).
  const { search } = useLocation();
  const [misuView, setMisuView] = useState<'cancel' | undefined>(undefined);
  useEffect(() => {
    const t = urlParam(search, 'tab');
    if (t === 'misu') {
      switchTab('misu');
      setMisuView(urlParam(search, 'view') === 'cancel' ? 'cancel' : undefined);
      return;
    }
    const recv = Number(urlParam(search, 'recv'));
    const m = urlParam(search, 'mon');
    if (t === 'cust' && Number.isInteger(recv) && recv > 0 && m && /^\d{4}-\d{2}$/.test(m)) { openCust(recv, m); return; }
    // 화면 찾기(#396) — 세부 탭만 고른 주소(`?tab=salary` 등)
    if ((t === 'cust' || t === 'salary' || t === 'status' || (t === 'summary' && canSummary)) && t !== tab) switchTab(t);
    // 주소가 바뀔 때만 — switchTab·openCust 는 렌더마다 새로 만들어진다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  // 새로 열면 `me` 가 늦게 와 위 판정 때 canSummary 가 아직 false — 권한이 도착하면 `?tab=summary` 를 한 번 더 적용한다.
  useEffect(() => {
    if (canSummary && urlParam(search, 'tab') === 'summary' && tab !== 'summary') switchTab('summary');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSummary]);
  useEffect(() => {
    if (!openRecv || tab !== 'cust' || loading) return;
    const hit = rows.find((r) => Number(r.recv_id) === openRecv.recvId && String(r.drive_mon) === openRecv.mon);
    if (hit) { setDetail(hit); setOpenRecv(null); }
  }, [openRecv, tab, loading, rows]);

  return (
    <div className="space-y-2">
      {/* 운행편성 변경(10-02) — 이 정산월에 편성이 바뀐 차량이 있으면 늘 보인다 */}
      <AllocChangeBanner mon={mon} compact={tab === 'cust'} />
      {/* 위칸은 **한 줄**로 둔다(2026-09-30 way 「위칸을 최대로 좁히고 아래 작업칸을 넓게」) — 설명은 제목 툴팁. */}
      <div className="btn-row-sm flex flex-wrap items-center gap-3">
        <h1
          className="cursor-help text-lg font-bold"
          title={'차가 다 배정된(배차완료) 건만 들어갑니다. 줄을 누르면 세부 노선·배정차량이 펼쳐집니다.\n일반은 배차에서 계산하고, 과거 통근은 당시 확정된 정산 스냅샷을 읽습니다.'}
        >
          정산 <span className="text-xs font-normal text-ink-faint">ⓘ</span>
        </h1>
        {/* 폰: 탭은 한 줄 가로 밀기(글자 두 줄 방지) · 검색 줄은 그 아래 꽉 차게. */}
        <div className="-mx-3 flex w-[calc(100%+1.5rem)] gap-1 overflow-x-auto px-3 md:mx-0 md:w-auto md:overflow-visible md:px-0">
          {SETTLE_TABS.filter(([id]) => id !== 'summary' || canSummary).map(([id, label]) => (
            <button
              key={id}
              onClick={() => switchTab(id)}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium ${
                tab === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQ(term.trim());
            setPage(0);
          }}
          className="flex w-full min-w-0 items-center gap-2 md:ml-auto md:w-auto"
        >
          {/* 미수금 탭은 자기 칸(운행 시작일·정산년월)만 쓴다 — 위 칸을 같이 두면 「정산년월이 두 개」로 보였다(#213). */}
          {tab !== 'misu' && (
            <DriveMonPicker
              months={tab === 'commute' ? withCommuteMonths(months) : tab === 'cust' ? withThisMonth(months) : months}
              value={mon}
              width="w-32"
              extra={tab === 'cust' ? { value: '', label: '전체 기간' } : undefined}
              onChange={(m) => {
                setMon(m);
                setPage(0);
                setDetail(null);
              }}
            />
          )}
          {/* 정산현황은 축(차주/직영)마다 검색 대상이 달라 자체 검색창을 쓴다. */}
          {tab !== 'status' && tab !== 'summary' && tab !== 'misu' && tab !== 'commute' && (
            <>
              <input
                className="field min-w-0 flex-1 md:w-56 md:flex-none"
                placeholder={
                  tab === 'cust'
                    ? '거래처 · 청구방법 · 버스인접수담당자 · 접수 NO'
                    : '기사 · 차주 · 소속 · 구분 · 번호'
                }
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
              <button className="btn-ghost" type="submit">
                검색
              </button>
            </>
          )}
        </form>
      </div>

      {/* 폰: 마감·통근 칸은 접어 둔다(2026-09-30 way 캡처 「윗공간 너무 넓고」 — 표가 화면 절반도 못 받았다). PC 는 늘 펼침. */}
      {tab === 'cust' && (
        <button type="button" className="btn-ghost w-full justify-between py-1.5 text-xs md:hidden" onClick={() => setTopOpen((v) => !v)}>
          <span>{mon || '전체 기간'} 마감 · 통근</span>
          <span>{topOpen ? '접기 ▲' : '펼치기 ▼'}</span>
        </button>
      )}
      {tab === 'cust' && (
        <div className={`${topOpen ? 'flex' : 'hidden'} flex-wrap items-start gap-2 md:flex`}>
          <SettleClose driveMon={mon} />
          <div className="min-w-0 flex-1 md:min-w-[36rem]">
            <CommuteMonthBar driveMon={mon} q={q} ready={!loading} nonce={nonce} onChanged={() => setNonce((n) => n + 1)} />
          </div>
        </div>
      )}
      {tab === 'salary' && (
        <div className="flex flex-wrap items-start gap-2">
          <SalaryGenPanel
            driveMon={mon}
            onDone={() => {
              setDetail(null);
              setNonce((n) => n + 1);
            }}
          />
          {/*
            공개 예약은 **명세 생성 바로 옆**에 둔다 — 만들고 다듬는 동안 잠가 두는 것이
            같은 작업의 일부다(2026-09-16 사용자 요청: "작업하니까 못 보도록").
          */}
          <button className="btn-ghost py-1 text-sm" onClick={() => setPublishOpen(true)}>
            기사 명세서 공개 관리
          </button>
        </div>
      )}

      {misuEdit && (
        <Modal>
          <MisuEditForm
            row={misuEdit}
            scope="sales"
            onClose={() => setMisuEdit(null)}
            onSaved={() => { setMisuEdit(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      {timeline && !misuEdit && !invReq && (
        <Modal width="max-w-2xl">
          <BillTimeline
            recvId={Number(timeline.recv_id)}
            driveMon={String(timeline.drive_mon ?? '')}
            custName={String(timeline.cust_name ?? '')}
            onClose={() => setTimeline(null)}
            onRequest={['', '청구대상'].includes(String(timeline.misu_req_status ?? '').trim()) ? () => setInvReq(timeline) : null}
            onInfo={String(timeline.misu_req_status ?? '').trim() !== '' ? () => setMisuEdit(timeline) : null}
          />
        </Modal>
      )}

      {invReq && (
        <Modal>
          <InvoiceRequestForm
            prefill={{ recvId: Number(invReq.recv_id), driveMon: String(invReq.drive_mon ?? '') }}
            onClose={() => setInvReq(null)}
            onSaved={() => { setInvReq(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      {publishOpen && (
        <Modal width="max-w-4xl">
          <SalaryPublishPanel onClose={() => setPublishOpen(false)} />
        </Modal>
      )}

      {/* 검색어가 월처럼 생겼는데 선택기가 다른 달이면 0건이 뜬다 — 그 이유를 그 자리에 적는다. */}
      {tab !== 'status' && tab !== 'summary' && tab !== 'misu' && tab !== 'commute' && (
        <MonthSearchHint
          q={q}
          mon={mon}
          months={months}
          onPick={(m) => { setMon(m); setPage(0); setDetail(null); }}
          onAll={tab === 'cust' ? () => { setMon(''); setPage(0); setDetail(null); } : undefined}
        />
      )}

      {error && <ErrorBox message={error} />}

      {tab === 'status' && <SettleStatus driveMon={mon} />}
      {tab === 'summary' && canSummary && <SettleSummary driveMon={mon} />}
      {/* 미수는 **기간 축이 운행시작일**이라 정산년월을 안 쓴다 — 자기 필터를 스스로 갖는다. */}
      {/* 월 목록은 **부모가 이미 갖고 있다.** 자식이 또 부르면 같은 무거운 조회가 동시에 두 번 나가고,
          실제로 그중 하나가 prod 에서 500 으로 죽어 미수 탭의 정산년월이 빈 채로 떴다(2026-08-01). */}
      {tab === 'misu' && <SettleMisu months={months} onOpenCust={openCust} initialView={misuView} />}
      {tab === 'commute' && <SettleCommute driveMon={mon} />}

      {/*
        거래처 청구 상세는 **인라인**이다 — 그 안에서 수당·공제 팝업을 또 여는 화면이라
        모달로 감싸면 팝업 위에 팝업이 겹친다(닫는 순서가 헷갈리고 뒤가 안 보인다).
        넓은 화면은 표 오른쪽(아래 `split`), 좁은 화면만 여기(표 위).
      */}
      {!split && custDetail}

      {/*
        급여명세는 **팝업**이다(2026-08-02 사용자 요청). 인라인 상세는 표 **위**에 열려서,
        기사가 수백 명이면 아래쪽 줄을 누른 뒤 상세를 보려고 **다시 맨 위로 스크롤**해야 했다.
        팝업은 누른 자리에서 뜨고, 뒤 페이지 스크롤을 잠가 "어느 쪽이 움직이는지" 헷갈리지 않는다.
        ⚠️ 여백·Esc 로 안 닫힌다(components/Modal.tsx 기본값) — 닫는 길은 폼의 `닫기` 뿐이다.
      */}
      {tab === 'salary' && detail && (
        <Modal width="max-w-5xl">
          <SalarySlip row={detail} onClose={() => setDetail(null)} />
        </Modal>
      )}

      {tab !== 'status' && tab !== 'summary' && tab !== 'misu' && tab !== 'commute' && (
        <div className={split ? 'grid items-start' : ''}
             style={split ? { gridTemplateColumns: `minmax(0,${leftW}px) 0.75rem minmax(0,1fr)` } : undefined}>
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {tab === 'cust' && (
            <>
              <select
                className="field w-36"
                aria-label="접수구분"
                title="접수구분 — 레거시 버스인 「접수구분」과 같습니다"
                value={pick.recvType}
                onChange={(e) => setPick({ ...pick, recvType: e.target.value as CustPick['recvType'] })}
              >
                <option value="">접수구분 전체</option>
                <option value="일반">일반</option>
                <option value="통근">통근</option>
              </select>
              <select
                className="field w-36"
                aria-label="청구법인"
                title="청구법인 — 접수에 적힌 청구법인(레거시 버스인 「청구법인」). 고른 법인만 나옵니다(지점 제외)."
                value={pick.finGrp === '' ? '' : String(pick.finGrp)}
                onChange={(e) => setPick({ ...pick, finGrp: e.target.value === '' ? '' : Number(e.target.value) })}
              >
                <option value="">청구법인 전체</option>
                {finGrps.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                {/* 목록이 오기 전·목록에서 사라진 기억값도 고른 상태로 보이게 */}
                {pick.finGrp !== '' && pick.finGrp !== 0 && !finGrps.some((g) => g.id === pick.finGrp) && (
                  <option value={pick.finGrp}>법인 #{pick.finGrp}</option>
                )}
                <option value="0">법인 미지정</option>
              </select>
            </>
          )}
          <label className="flex w-fit items-center gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={grouped}
              onChange={(e) => {
                setGrouped(e.target.checked);
                if (e.target.checked) setBillGrouped(false);
                setPage(0);
                setDetail(null);
              }}
            />
            {tab === 'cust' ? '거래처별로 묶어 소계 보기' : '소속별로 묶어 소계 보기'}
            <span className="hidden text-xs text-ink-faint md:inline">— 켜면 목록이 그 순서로 다시 정렬됩니다</span>
          </label>
          {tab === 'cust' && (
            <label className="flex w-fit items-center gap-2 text-sm text-ink-soft"
                   title="통합 청구로 묶인 접수를 묶음 이름 한 줄(합계)로 보여 줍니다. 그 줄을 누르면 묶음 안 접수가 펼쳐집니다.">
              <input
                type="checkbox"
                checked={billGrouped}
                onChange={(e) => {
                  setBillGrouped(e.target.checked);
                  // 묶음은 거래처를 가로지른다 — 거래처 소계와 같이 켜면 소계 줄이 묶음을 쪼갠다.
                  if (e.target.checked) setGrouped(false);
                  setFoldOpen(new Set());
                  setPage(0);
                  setDetail(null);
                }}
              />
              통합 청구 묶음으로 보기
            </label>
          )}
          </div>

          <DataTable
            columns={split ? splitCols : tab === 'cust' ? custCols : SALARY_COLUMNS}
            // ⚠️ 탭마다 칼럼이 다르므로 **탭까지 구분**한다 — 안 그러면 한쪽 설정이 다른 쪽을 덮는다.
            //    2026-08-02 까지 이 표에만 tableId 가 없어 칼럼 조절이 안 떴다(표 18곳 중 유일).
            //    좌우 2단의 좁은 목록도 따로(넓은 표에서 숨긴 칸 설정이 좁은 목록 칸을 지우지 않게).
            tableId={split ? 'settle.cust.split' : tab === 'cust' ? 'settle.cust' : 'settle.salary'}
            exportXlsx={{
              name: `${tab === 'cust' ? '거래처별정산' : '기사급여명세'}_${mon || '전체'}`,
              loadAll: () => loadAllPages((limit, offset) =>
                (tab === 'cust' ? api.listResultCust({ ...args, limit, offset }) : api.listSalaryMonths({ ...args, limit, offset }))
                  .then((r) => r.rows as unknown as Row[])),
            }}
            rows={rows}
            loading={loading}
            {...(tab === 'cust' ? {
              sort: custSort,
              onSort: (k: string) => { toggleCustSort(k); setPage(0); setDetail(null); },
              filters: custFilterDraft,
              onFilter: (key: string, value: string) => setCustFilterDraft((cur) => ({ ...cur, [key]: value })),
            } : {})}
            onRowClick={(r) => setDetail((cur) => (cur === r ? null : r))}
            activeRow={(r) => detail === r}
            total={total}
            // 한 달이 244행이면 아래까지 내려가야 총계가 보인다 — 머리글 바로 밑에 둔다.
            // ⚠️ 소계는 그대로 그룹 사이에 둔다(어느 묶음의 합인지는 그 자리에서만 읽힌다).
            totalAt="top"
            subtotals={grouped ? subtotals : null}
            folds={billGroup ? {
              of: (r) => (r.bill_group_key ? String(r.bill_group_key) : null),
              rows: billGroups,
              open: foldOpen,
              toggle: (k) => setFoldOpen((cur) => {
                const n = new Set(cur);
                if (n.has(k)) n.delete(k); else n.add(k);
                return n;
              }),
              labelKey: 'cust_name',
              badge: (g) => (
                <span className="shrink-0 rounded-full bg-violet-100 px-1.5 text-[11px] font-medium leading-5 text-violet-700">묶음 {g.count}</span>
              ),
            } : null}
            rowClass={billGroup ? (r) => (r.bill_group_key ? 'bg-violet-50/40' : '') : undefined}
            groupOf={
              grouped
                ? (r) =>
                    tab === 'cust'
                      ? String(r.cust_name ?? '') || '(거래처 없음)'
                      : String(r.grp_name ?? '') || '(소속 없음)'
                : undefined
            }
          />
          <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
        </div>
        {split && <SplitDivider width={leftW} onDrag={setDragW} onDone={(w) => { setDragW(null); setSplitW(w); }} />}
        {split && <div className="sticky top-2 min-w-0">{custDetail}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * 급여명세서 한 장.
 *
 * ⚠️ 여기 보이는 줄에는 `io='운행료'` 도 섞여 있다. 직영기사(255명)의 운행료가 그것인데
 *    **지급이 아니라 기록용 정보행**이라 차인지급액 합계에는 들어가지 않는다.
 *    화면에서도 그 사실을 적어 둔다 — 안 적으면 "합이 안 맞는다"는 문의가 온다.
 */
/** 명세서 머리의 금액 한 칸. 세 칸이 `지급 − 공제 = 차인지급액` 으로 읽히게 나란히 선다. */
function SlipBox({ label, value, tone, strong }: {
  label: string; value: unknown; tone?: string; strong?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-1.5 text-right ${
        strong ? 'border-brand-200 bg-brand-50' : 'border-line bg-surface'
      }`}
    >
      <div className="text-xs text-ink-faint">{label}</div>
      <div className={`${strong ? 'text-base font-bold' : 'text-sm font-semibold'} ${tone ?? ''}`}>
        {money(value)}
      </div>
    </div>
  );
}

/** 차인지급액 조정(#290)도 같은 명세서를 띄운다 — 정산 화면과 한 벌. */
export function SalarySlip({ row, onClose }: { row: Row; onClose: () => void }) {
  const driveMon = String(row.drive_mon ?? '');
  const driverId = row.driver_id === null || row.driver_id === undefined ? undefined : Number(row.driver_id);

  // 차량·차주 묶음(월 중간 편성 변경 — 둘 이상일 때만 서버가 준다). 기사 화면 시뮬레이터가 기사앱처럼 나눠 그린다.
  const [groups, setGroups] = useState<SlipGroup[]>([]);
  const [remain, setRemain] = useState<SalaryRemain[]>([]);
  const { rows, loading, error, total, subtotals } = useList(
    (alive) => asRows(api.listSalaryLines({ driveMon, driverId, limit: 5000 }) /* 한 장 전부(10-15 — 한 달 최대 500줄 실측, 200 이면 잘린다) */.then((r) => {
      if (alive()) {
        setGroups((r as { groups?: SlipGroup[] }).groups ?? []);
        setRemain(r.remain ?? []);
      }
      return r;
    })),
    [driveMon, driverId],
  );

  const hasInfoLine = rows.some((r) => r.io === '운행료');

  /**
   * **기사 화면 시뮬레이터**(2026-09-16 사용자 요청 — "명세서가 어떻게 보이는지 한눈에 점검").
   *
   * 🔴 기사앱과 **같은 컴포넌트**(`driver/SalarySlip`)를 같은 데이터로 그린다. 비슷하게 베껴
   *    만들면 그 순간부터 두 화면이 갈리고 "점검했는데 기사 화면은 다르더라"가 된다 —
   *    점검 도구로서 의미가 사라진다.
   * ⚠️ 기사앱 API(`listMySalaryLines`)는 **본인 토큰**으로만 열리므로 담당자가 못 부른다.
   *    그래서 데이터는 담당자 API 로 가져오되 **컬럼 이름이 같아서** 그대로 꽂힌다
   *    (`io`·`category`·`account_name`·`amt`·`comment`). 이름이 어긋나면 여기서 티가 난다.
   */
  const [asDriver, setAsDriver] = useState(false);

  return (
    <div className="card space-y-3 p-4">
      {/*
        명세서 머리 — 누구의 몇 월치인지, 그리고 **지급 − 공제 = 차인지급액**이 그 자리에서 읽혀야 한다.
        예전에는 이름과 차인지급액이 한 줄에 붙어 있어 "이 금액이 어디서 나왔나"를 표를 훑어야 알았다.
      */}
      <div className="flex flex-wrap items-start gap-4 rounded-lg border border-line bg-canvas p-3">
        <div>
          <div className="text-xs text-ink-faint">{driveMon} 급여명세</div>
          <div className="text-lg font-bold">{dash(row.driver_name)}</div>
          <div className="mt-0.5 text-xs text-ink-soft">
            {dash(row.kind)} · {dash(kindLabel(row.driver_kind))}
            {row.owner_name ? <> · 차주 {String(row.owner_name)}</> : null}
            {row.grp_name ? <> · {String(row.grp_name)}</> : null}
          </div>
        </div>

        {/* 합계는 **서버가 낸 값 그대로**다 — 화면의 줄을 더하지 않는다(상한에 걸리면 틀린 숫자가 맞는 척한다). */}
        <div className="ml-auto flex items-stretch gap-2 tabular-nums">
          {/* 전월누적분잔액(#262) — 구 버스인 명세서 머리 칸. 차인지급액 식과 별개라 따로 띄운다. 차주 명세만. */}
          {remain.map((m) => (
            <SlipBox
              key={m.owner_id}
              label={remain.length > 1 ? `전월누적분잔액 · ${m.owner_name ?? ''}` : '전월누적분잔액'}
              value={m.amt}
              tone={Number(m.amt) < 0 ? 'text-red-700' : undefined}
            />
          ))}
          {remain.length > 0 && <span className="mx-1 w-px self-stretch bg-line" />}
          <SlipBox label="지급 계" value={total?.values.plus_amt} tone="text-emerald-700" />
          <span className="self-center text-ink-faint">−</span>
          <SlipBox label="공제 계" value={total?.values.minus_amt} tone="text-red-700" />
          <span className="self-center text-ink-faint">=</span>
          <SlipBox label="차인지급액" value={total?.values.amt ?? row.result_amt} strong />
        </div>

        <div className="flex gap-2 self-start">
          <button
            className={asDriver ? 'btn-primary' : 'btn-ghost'}
            onClick={() => setAsDriver((v) => !v)}
            title="기사앱에서 이 명세서가 어떻게 보이는지 그대로 그린다"
          >
            {asDriver ? '표로 보기' : '기사 화면으로 보기'}
          </button>
          <button className="btn-ghost" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      {asDriver ? (
        /*
          폰 폭(390px)으로 가둬 그린다 — 넓은 칸에 그리면 줄바꿈·잘림이 실제와 달라
          "점검했다"가 거짓이 된다. 기사앱은 `driver-card`·큰 글씨 규약을 쓰므로
          그 클래스가 그대로 살아 있어야 한다(`globals.css` 의 기사앱 전용 클래스).
        */
        <div className="flex flex-col items-center gap-2 py-2">
          <p className="text-xs text-ink-faint">
            기사앱(폰 390px)에서 보이는 모습입니다 — 같은 컴포넌트로 그립니다.
          </p>
          <div className="w-[390px] rounded-2xl border-2 border-line bg-canvas p-4">
            {loading ? (
              <p className="py-8 text-center text-sm text-ink-faint">불러오는 중…</p>
            ) : (
              <DriverSlip
                lines={rows as { io?: string | null }[]}
                groups={groups}
                summary={{
                  plus_amt: total?.values.plus_amt,
                  minus_amt: total?.values.minus_amt,
                  result_amt: total?.values.amt ?? row.result_amt,
                }}
              />
            )}
          </div>
        </div>
      ) : null}

      {/* 아래는 담당자 시선(표). 기사 화면을 보는 동안에는 접는다 — 두 벌이 같이 보이면
          "어느 쪽이 기사가 보는 것인지"가 흐려져 점검이 안 된다. */}
      {!asDriver && hasInfoLine && (
        <p className="text-xs text-amber-700">
          ‘운행료’ 줄은 기록용이라 차인지급액 합계에 들어가지 않습니다(관리기사A 항목).
        </p>
      )}

      {/* 지급·공제 소계가 **줄 사이에** 선다 — 명세서는 두 덩어리를 각각 합쳐 보는 문서다.
          목록이 `io desc` 로 정렬돼 있어 소계 키와 순서가 맞는다(어긋나면 소계가 흩어진다). */}
      {!asDriver && (
        <DataTable
          columns={LINE_COLUMNS}
          rows={rows}
          loading={loading}
          total={total}
          subtotals={subtotals}
          groupOf={(r) => String(r.io ?? '')}
          tableId="settle.salaryLines"
        />
      )}
      {!asDriver && total && Number(total.values.info_amt ?? 0) !== 0 && (
        <p className="text-xs text-ink-soft">
          운행료(정보행, 합계 제외) {money(total.values.info_amt)}
        </p>
      )}
    </div>
  );
}

const SPLIT_W0 = 512;
const SPLIT_MIN = 320;
const isSplitW = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= SPLIT_MIN;
/** 좌우 2단 경계선(#457) — 끄는 동안은 화면만, 놓을 때 한 번 저장 · 오른쪽 상세는 최소 600px 남긴다 · 두 번 누르면 기본 폭. */
function SplitDivider({ width, onDrag, onDone }: { width: number; onDrag: (w: number) => void; onDone: (w: number) => void }) {
  const start = useRef<{ x: number; w: number; last: number } | null>(null);
  const clamp = (w: number) => Math.round(Math.max(SPLIT_MIN, Math.min(w, window.innerWidth - 600)));
  return (
    <div role="separator" aria-orientation="vertical" title="끌어서 왼쪽 목록 폭 조절 · 두 번 누르면 기본 폭"
      className="group sticky top-2 flex h-[calc(100vh-6rem)] cursor-col-resize touch-none select-none justify-center"
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); start.current = { x: e.clientX, w: width, last: width }; }}
      onPointerMove={(e) => { const st = start.current; if (!st) return; st.last = clamp(st.w + e.clientX - st.x); onDrag(st.last); }}
      onPointerUp={() => { const st = start.current; start.current = null; if (st) onDone(st.last); }}
      onPointerCancel={() => { const st = start.current; start.current = null; if (st) onDone(st.w); }}
      onDoubleClick={() => onDone(SPLIT_W0)}>
      <div className="w-1 rounded-full bg-line group-hover:bg-brand-400" />
    </div>
  );
}
